import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { 
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  Eraser,
  AlertTriangle,
  Minus
} from "lucide-react";

interface FormSection {
  id: string;
  icon: React.ReactNode;
  label: string;
  content: React.ReactNode;
  hasTemplate?: boolean;
  hasClear?: boolean;
  required?: boolean;
}

interface FormSectionWrapperProps {
  section: FormSection;
  isMinimized: boolean;
  hasErrors: boolean;
  sectionCount?: number | null;
  hasData?: boolean;
  onMinimize: (sectionId: string) => void;
  onExpand: (sectionId: string) => void;
  onLoadTemplate?: (type: string) => void;
  onSaveTemplate?: (type: string) => void;
  onClearSection?: (sectionId: string, label: string) => void;
}

// Clean minimized section - matching screenshot
const MinimizedSection = ({ 
  section, 
  sectionCount, 
  hasData, 
  hasErrors,
  onExpand 
}: {
  section: FormSection;
  sectionCount?: number | null;
  hasData?: boolean;
  hasErrors?: boolean;
  onExpand: (sectionId: string) => void;
}) => {
  return (
    <div 
      className={cn(
        "flex items-center justify-between py-3 px-4 bg-card border border-border rounded-xl mb-3 cursor-pointer transition-all duration-200 hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm",
        hasErrors && "border-destructive/30 bg-destructive/5"
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onExpand(section.id);
      }}
      title={`Click to expand ${section.label}`}
    >
      <div className="flex items-center gap-3">
        <span className={cn(
          "text-muted-foreground transition-colors",
          hasErrors && "text-destructive"
        )}>
          {section.icon}
        </span>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-foreground text-sm">{section.label}</span>
          {section.required && <span className="text-destructive text-xs font-medium">*</span>}
        </div>
        
        {/* Enterprise status indicators */}
        <div className="flex items-center gap-2">
          {(sectionCount !== null && sectionCount !== undefined && sectionCount > 0) && (
            <span className="px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold min-w-[20px] text-center">
              {sectionCount}
            </span>
          )}
          {hasData && (sectionCount === null || sectionCount === 0) && (
            <div className="w-2 h-2 bg-success rounded-full" />
          )}
          {hasErrors && (
            <AlertTriangle className="w-4 h-4 text-destructive" />
          )}
        </div>
      </div>
      
      <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-hover:text-foreground" />
    </div>
  );
};

// Enterprise-level expanded section with icon-only actions
const ExpandedSection = ({ 
  section, 
  hasErrors,
  sectionCount,
  hasData,
  onMinimize,
  onLoadTemplate,
  onSaveTemplate,
  onClearSection
}: {
  section: FormSection;
  hasErrors: boolean;
  sectionCount?: number | null;
  hasData?: boolean;
  onMinimize: (sectionId: string) => void;
  onLoadTemplate?: (type: string) => void;
  onSaveTemplate?: (type: string) => void;
  onClearSection?: (sectionId: string, label: string) => void;
}) => {
  const getTemplateType = (sectionId: string) => {
    console.log('Getting template type for section:', sectionId);
    const type = (() => {
      switch(sectionId) {
        case "complaints": return "complaint";
        case "medicines": return "medicine";
        case "investigations": return "investigation";
        case "advice": return "advice";
        case "diagnosis": return "diagnosis";
        case "notes": return "quick_notes";
        case "next-visit": return "quick_notes";
        default: 
          console.warn(`Unknown section ID for template mapping: ${sectionId}`);
          return "quick_notes";
      }
    })();
    console.log('Template type:', type);
    return type;
  };

  return (
    <div className={cn(
      "bg-card border border-border rounded-xl mb-4 shadow-sm",
      hasErrors && "border-destructive/30 shadow-destructive/10"
    )}>
      {/* Enterprise Left-Right Layout */}
      <div className="flex">
        {/* Left Side - Compact Professional Controls */}
        <div className={cn(
          "w-40 flex-shrink-0 bg-gradient-to-b from-muted/50 to-muted/30 border-r border-border p-3 flex flex-col self-stretch",
          hasErrors && "from-destructive/10 to-destructive/5 border-destructive/30"
        )}>
          {/* Section Header */}
          <div className="mb-3">
            <h3 className="text-sm font-medium text-foreground">
              {section.label === "Complaints" ? "Chief Complaints" : section.label}
            </h3>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-1.5 mb-3">
            {/* Load Template */}
            {section.hasTemplate && onLoadTemplate && (
              <button
                type="button"
                onClick={() => {
                  console.log('Loading template for section:', section.id);
                  onLoadTemplate(getTemplateType(section.id));
                }}
                className="flex items-center justify-center w-7 h-7 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-200"
                title="Load Template"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            )}
            
            {/* Save Template */}
            {section.hasTemplate && onSaveTemplate && (
              <button
                type="button"
                onClick={() => onSaveTemplate(getTemplateType(section.id))}
                className="flex items-center justify-center w-7 h-7 text-muted-foreground hover:text-success hover:bg-success/10 rounded-lg transition-all duration-200"
                title="Save as Template"
              >
                <Upload className="w-3.5 h-3.5" />
              </button>
            )}
            
            {/* Clear Section */}
            {section.hasClear && onClearSection && (
              <button
                type="button"
                onClick={() => onClearSection(section.id, section.label)}
                className="flex items-center justify-center w-7 h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                title="Clear Section"
              >
                <Eraser className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          
          {/* Minimize Button */}
          <div className="mt-auto">
            <button
              type="button"
              onClick={() => onMinimize(section.id)}
              className="flex items-center gap-1.5 w-full justify-center text-muted-foreground hover:text-foreground py-1.5 rounded-md hover:bg-accent transition-colors duration-200"
              title="Minimize section"
            >
              <ChevronUp className="w-3 h-3" />
              <span className="text-xs font-medium">minimise</span>
            </button>
          </div>
        </div>
        
        {/* Right Side - Content Area */}
        <div className="flex-1 p-4 ">
          {section.content}
        </div>
      </div>
    </div>
  );
};

export default function FormSectionWrapper({
  section,
  isMinimized,
  hasErrors,
  sectionCount,
  hasData,
  onMinimize,
  onExpand,
  onLoadTemplate,
  onSaveTemplate,
  onClearSection
}: FormSectionWrapperProps) {
  if (isMinimized) {
    return (
      <MinimizedSection
        section={section}
        sectionCount={sectionCount}
        hasData={hasData}
        hasErrors={hasErrors}
        onExpand={onExpand}
      />
    );
  }

  return (
    <ExpandedSection
      section={section}
      hasErrors={hasErrors}
      sectionCount={sectionCount}
      hasData={hasData}
      onMinimize={onMinimize}
      onLoadTemplate={onLoadTemplate}
      onSaveTemplate={onSaveTemplate}
      onClearSection={onClearSection}
    />
  );
} 